import React, { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { format, startOfToday } from 'date-fns';
import { toast } from 'react-hot-toast';
import {
  GET_MY_PTO_DAYS_REMAINING,
  GET_MY_VACATIONS,
} from '../../graphql/my-availability-queries';
import { DELETE_MY_VACATION } from '../../graphql/my-availability-mutations';
import MyTimeOffFormModal from './MyTimeOffFormModal';
import VacationApprovalBadge from './VacationApprovalBadge';
import {
  compareCalendarDates,
  getVacationDeleteConfirmation,
  toCalendarDateKey,
  toLocalCalendarDate,
} from '../../utils/availabilityDateUtils';

const VACATION_TYPE_LABELS = {
  PTO: 'PTO',
  SICK: 'Sick',
  PERSONAL: 'Personal',
  UNPAID: 'Unpaid',
  OTHER: 'Other'
};

function MyTimeOffCard({ onChange }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingVacation, setEditingVacation] = useState(null);

  // Query a generous forward window for "upcoming" entries (today through ~3 years out).
  const today = useMemo(() => startOfToday(), []);
  const horizon = useMemo(() => {
    const date = new Date(today);
    date.setFullYear(date.getFullYear() + 3);
    return date;
  }, [today]);

  const { data, loading, refetch } = useQuery(GET_MY_VACATIONS, {
    variables: {
      startDate: today.toISOString(),
      endDate: horizon.toISOString()
    },
    fetchPolicy: 'network-only'
  });
  const { data: ptoBalanceData, loading: ptoBalanceLoading, refetch: refetchPtoBalance } = useQuery(
    GET_MY_PTO_DAYS_REMAINING,
    { fetchPolicy: 'network-only' },
  );
  const ptoDaysRemaining = ptoBalanceData?.myPtoDaysRemaining;

  const [deleteMyVacation, { loading: deleting }] = useMutation(DELETE_MY_VACATION, {
    onCompleted: () => {
      toast.success('Time off removed');
      refetch();
      refetchPtoBalance();
      onChange?.();
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to remove time off');
    }
  });

  // Surface only entries whose end date is today or later (in case the query overlaps past entries).
  const upcoming = useMemo(() => {
    const all = data?.myVacations || [];
    const todayKey = toCalendarDateKey(today);
    return [...all]
      .filter((entry) => toCalendarDateKey(entry.endDate) >= todayKey)
      .sort((a, b) => compareCalendarDates(a.startDate, b.startDate));
  }, [data, today]);

  const handleAdd = () => {
    setEditingVacation(null);
    setIsModalOpen(true);
  };

  const handleEdit = (vacation) => {
    setEditingVacation(vacation);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingVacation(null);
  };

  const handleSaved = () => {
    handleCloseModal();
    refetch();
    refetchPtoBalance();
    onChange?.();
  };

  const handleDelete = (vacation) => {
    const message = getVacationDeleteConfirmation(vacation);
    if (!window.confirm(message)) return;
    deleteMyVacation({ variables: { id: vacation.id } });
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6">
        <div className="flex items-center justify-between gap-3 mb-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-lg font-semibold text-gray-900">Upcoming Time Off</h2>
            <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${
              ptoDaysRemaining == null
                ? 'border-gray-200 bg-gray-100 text-gray-600'
                : 'border-violet-200 bg-violet-50 text-violet-800'
            }`}>
              PTO remaining: {ptoBalanceLoading
                ? 'Loading…'
                : ptoDaysRemaining == null
                  ? 'Not Set'
                  : `${ptoDaysRemaining} ${ptoDaysRemaining === 1 ? 'day' : 'days'}`}
            </span>
          </div>
          <button
            type="button"
            onClick={handleAdd}
            className="inline-flex items-center px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-sky-600 hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500"
          >
            <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Time Off
          </button>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          Requests must be approved by an administrator before they appear on your calendar or affect availability.
        </p>

        {loading ? (
          <div className="py-8 text-center text-gray-500">Loading...</div>
        ) : upcoming.length === 0 ? (
          <div className="py-8 text-center text-gray-500">
            No upcoming time off scheduled. Click "Add Time Off" to add some.
          </div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {upcoming.map((vacation) => {
              const start = toLocalCalendarDate(vacation.startDate);
              const end = toLocalCalendarDate(vacation.endDate);
              const isSameDay = toCalendarDateKey(start) === toCalendarDateKey(end);
              const typeLabel = VACATION_TYPE_LABELS[vacation.vacationType] || vacation.vacationType || 'Time Off';
              return (
                <li
                  key={vacation.id}
                  className="py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2"
                >
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-gray-900">
                      {isSameDay
                        ? format(start, 'EEE, MMM d, yyyy')
                        : `${format(start, 'EEE, MMM d')} – ${format(end, 'EEE, MMM d, yyyy')}`}
                    </span>
                    <span className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800 mr-2">
                        {typeLabel}
                      </span>
                      <VacationApprovalBadge status={vacation.approvalStatus} />
                      {vacation.reason && <span className="text-gray-600">{vacation.reason}</span>}
                    </span>
                    {vacation.reviewNote && (
                      <span className="mt-1 text-xs text-gray-600">
                        Administrator note: {vacation.reviewNote}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handleEdit(vacation)}
                      className="text-sm font-medium text-sky-600 hover:text-sky-800"
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(vacation)}
                      disabled={deleting}
                      className="text-sm font-medium text-red-600 hover:text-red-800 disabled:opacity-50"
                    >
                      Delete
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <MyTimeOffFormModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        vacation={editingVacation}
        onSaved={handleSaved}
      />
    </div>
  );
}

export default MyTimeOffCard;
